import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

import { api } from "@/lib/pp/api";
import { toLocalYMD, fromYMD, todayYMD } from "@/lib/pp/dates";

const emptyForm = {
  first_name: "",
  last_name: "",
  client_id: "",
  registration_sent: false,
  registration_sent_date: null,
};

const REQUIRED = ["first_name", "last_name", "client_id"];

/**
 * Patient = the person (identity only). Dispensing records live on the
 * separate RecordFormDialog. This dialog collects only first name, last name,
 * client ID, and the one-time registration toggle.
 */
export default function PatientFormDialog({
  open,
  onOpenChange,
  editing,
  pharmacyId,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [regDateOpen, setRegDateOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const parts = (editing.patient_name || "").trim().split(/\s+/);
      setForm({
        ...emptyForm,
        first_name: editing.first_name || parts[0] || "",
        last_name: editing.last_name || parts.slice(1).join(" ") || "",
        client_id: editing.client_id || "",
        registration_sent: !!editing.registration_sent,
        registration_sent_date: editing.registration_sent_date || null,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [open, editing]);

  const upd = (k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: false } : prev));
  };
  const errCls = (k) =>
    errors[k]
      ? "border-[#B84A4A] ring-1 ring-[#B84A4A]/40 focus-visible:ring-[#B84A4A]"
      : "";

  const save = async () => {
    const nextErr = {};
    REQUIRED.forEach((k) => {
      const v = form[k];
      if (v === undefined || v === null || String(v).trim() === "")
        nextErr[k] = true;
    });
    if (Object.keys(nextErr).length) {
      setErrors(nextErr);
      toast.error("Please complete all required fields");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        patient_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        client_id: form.client_id.trim(),
        registration_sent: !!form.registration_sent,
        registration_sent_date: form.registration_sent
          ? form.registration_sent_date || todayYMD()
          : null,
      };
      if (editing) {
        await api.patch(`/patients/${editing.id}`, payload);
        toast.success("Patient updated");
      } else {
        await api.post(`/pharmacies/${pharmacyId}/patients`, payload);
        toast.success("Patient added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "h-10";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-white"
        data-testid="pp-patient-form-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-left">
          <DialogTitle
            className="text-[#1C2B26]"
            style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
          >
            {editing ? "Edit patient" : "Add patient"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-[#4A5D55]">
                First name *
              </Label>
              <Input
                value={form.first_name}
                onChange={upd("first_name")}
                data-testid="pp-patient-first-name"
                className={`${inputCls} ${errCls("first_name")}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-[#4A5D55]">
                Last name *
              </Label>
              <Input
                value={form.last_name}
                onChange={upd("last_name")}
                data-testid="pp-patient-last-name"
                className={`${inputCls} ${errCls("last_name")}`}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-[#4A5D55]">
              Client ID *
            </Label>
            <Input
              value={form.client_id}
              onChange={upd("client_id")}
              placeholder=""
              data-testid="pp-patient-client-id"
              className={`${inputCls} ${errCls("client_id")}`}
            />
          </div>

          <div className="flex items-center flex-wrap gap-x-2 gap-y-2 pt-1 min-h-[36px]">
            <Checkbox
              checked={!!form.registration_sent}
              onCheckedChange={(v) =>
                setForm((f) => ({
                  ...f,
                  registration_sent: !!v,
                  registration_sent_date: v
                    ? f.registration_sent_date || todayYMD()
                    : null,
                }))
              }
              data-testid="pp-patient-reg-sent"
              className="shrink-0"
            />
            <Label className="text-sm text-[#1C2B26] leading-none cursor-pointer m-0 flex-1 min-w-0 whitespace-nowrap">
              Registration sent
            </Label>
            {/* Date pill is always rendered so the row height stays fixed —
                just hidden when the checkbox is off. Prevents the dialog
                (and everything above) from shifting on toggle. */}
            <div style={{ visibility: form.registration_sent ? "visible" : "hidden" }}>
              <Popover open={regDateOpen} onOpenChange={setRegDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="pp-patient-reg-sent-date"
                    className="shrink-0 text-xs inline-flex items-center gap-1.5 border border-[#E5E2DC] bg-white rounded-md px-2.5 py-1.5 text-[#1C2B26]"
                  >
                    <CalendarIcon className="h-3 w-3 text-[#4A5D55]" />
                    {form.registration_sent_date
                      ? format(fromYMD(form.registration_sent_date), "MMM d, yyyy")
                      : "Set date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={fromYMD(form.registration_sent_date)}
                    onSelect={(d) => {
                      setForm((f) => ({ ...f, registration_sent_date: toLocalYMD(d) }));
                      setRegDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        <div className="flex flex-row items-center justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="pp-patient-cancel"
            className="h-9 px-4 text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            className="h-9 px-4 text-sm bg-[#0F3B2E] hover:bg-[#0A2A20] text-white"
            data-testid="pp-patient-save"
          >
            {busy ? "Saving…" : editing ? "Save changes" : "Add patient"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
